import { useState, useEffect, useCallback } from "react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import {
  Shield, ShieldAlert, ShieldCheck, Activity, Users, Lock, Mail,
  Eye, AlertTriangle, CheckCircle, XCircle, Globe, Clock, RefreshCw, History,
} from "lucide-react";
import { Link } from "react-router-dom";
import ImpersonationLogTab from "./security/ImpersonationLogTab";

const SEVERITY_STYLES = {
  critical: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", badge: "bg-red-100 text-red-700" },
  warning: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", badge: "bg-amber-100 text-amber-700" },
  info: { bg: "bg-blue-50", border: "border-blue-100", text: "text-blue-600", badge: "bg-blue-100 text-blue-600" },
};

function StatCard({ icon: Icon, label, value, sublabel, color = "text-gray-900", bgColor = "bg-white" }) {
  return (
    <div className={`${bgColor} border rounded-xl p-4`} data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
        <div>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
          <p className="text-xs text-gray-500">{label}</p>
          {sublabel && <p className="text-[10px] text-gray-400">{sublabel}</p>}
        </div>
      </div>
    </div>
  );
}

function LoginChart({ data }) {
  if (!data || data.length === 0) return null;
  const maxVal = Math.max(...data.map(d => d.success + d.failed), 1);

  return (
    <div className="bg-white border rounded-xl p-4" data-testid="login-chart">
      <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
        <Activity className="w-4 h-4 text-indigo-500" /> Connexions par jour
      </h3>
      <div className="flex items-end gap-1 h-32">
        {data.map(d => {
          const total = d.success + d.failed;
          const h = Math.max((total / maxVal) * 100, 4);
          const successH = total > 0 ? (d.success / total) * h : 0;
          const failH = h - successH;
          return (
            <div key={d.date} className="flex-1 flex flex-col items-center gap-0.5" title={`${d.date}: ${d.success} OK, ${d.failed} echecs`}>
              <div className="w-full flex flex-col rounded-t overflow-hidden" style={{ height: `${h}%` }}>
                {failH > 0 && <div className="bg-red-400 w-full" style={{ height: `${failH}%` }} />}
                {successH > 0 && <div className="bg-emerald-500 w-full flex-1" style={{ height: `${successH}%` }} />}
              </div>
              <span className="text-[8px] text-gray-400 rotate-[-45deg] origin-top-left whitespace-nowrap mt-1">{d.date.slice(5)}</span>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-4 mt-3 text-[10px] text-gray-500">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Reussis</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400" /> Echoues</span>
      </div>
    </div>
  );
}

function EventList({ events, title, icon: Icon, iconColor, emptyMsg }) {
  return (
    <div className="bg-white border rounded-xl overflow-hidden" data-testid={`events-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="px-4 py-3 border-b bg-gray-50 flex items-center gap-2">
        <Icon className={`w-4 h-4 ${iconColor}`} />
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
        <span className="text-xs text-gray-400 ml-auto">{events.length}</span>
      </div>
      <div className="max-h-72 overflow-y-auto divide-y">
        {events.map((e, i) => {
          const sev = SEVERITY_STYLES[e.severity] || SEVERITY_STYLES.info;
          return (
            <div key={e.id || i} className={`px-4 py-2.5 ${sev.bg}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${sev.badge}`}>{e.severity || "info"}</span>
                    <span className="text-xs font-medium text-gray-700 truncate">{e.event || e.login_type || "event"}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">
                    {e.detail
                      || (e.identifier ? `${e.identifier} — ${e.success ? "OK" : "Echec"}` : null)
                      || e.source
                      || e.email
                      || "—"}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-[10px] text-gray-400">{e.ip || ""}</p>
                  <p className="text-[10px] text-gray-300">{formatTime(e.timestamp)}</p>
                </div>
              </div>
            </div>
          );
        })}
        {events.length === 0 && (
          <div className="px-4 py-8 text-center text-gray-400 text-sm">{emptyMsg || "Aucun evenement"}</div>
        )}
      </div>
    </div>
  );
}

function formatTime(ts) {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" });
  } catch { return ts.slice(0, 16); }
}

function EventsTabContent({ data, ss }) {
  const d = data || {};
  const [sevFilter, setSevFilter] = useState("");
  const [textFilter, setTextFilter] = useState("");

  const all = d.recent_events || [];

  const matches = (e) => {
    if (sevFilter && (e.severity || "info") !== sevFilter) return false;
    if (textFilter) {
      const s = textFilter.toLowerCase();
      const hay = `${e.event || ""} ${e.detail || ""} ${e.identifier || ""} ${e.ip || ""}`.toLowerCase();
      if (!hay.includes(s)) return false;
    }
    return true;
  };

  const filtered = all.filter(matches);
  const accessDenied = (d.access_denied_events || []).filter(matches);
  const warnings = (d.warning_events || []).filter(matches);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={ShieldAlert} label="Critiques" value={ss.critical_count || 0} color={ss.critical_count > 0 ? "text-red-600" : "text-gray-400"} />
        <StatCard icon={AlertTriangle} label="Avertissements" value={ss.warning_count || 0} color={ss.warning_count > 0 ? "text-amber-600" : "text-gray-400"} />
        <StatCard icon={Eye} label="Acces refuses" value={ss.access_denied_count || 0} color={ss.access_denied_count > 0 ? "text-red-500" : "text-gray-400"} />
        <StatCard icon={Activity} label="Activite portail" value={ss.portal_activity_count || 0} color="text-indigo-500" />
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-xl p-3 flex flex-wrap items-center gap-2" data-testid="security-events-filters">
        <select value={sevFilter} onChange={e => setSevFilter(e.target.value)}
          className="border rounded-lg px-2 py-1.5 text-xs" data-testid="security-sev-filter">
          <option value="">Toutes severites</option>
          <option value="critical">Critique</option>
          <option value="warning">Avertissement</option>
          <option value="info">Info</option>
        </select>
        <input type="text" value={textFilter} onChange={e => setTextFilter(e.target.value)}
          placeholder="Rechercher (event, detail, IP, identifiant)..."
          className="border rounded-lg px-3 py-1.5 text-xs flex-1 min-w-[220px]"
          data-testid="security-text-filter" />
        {(sevFilter || textFilter) && (
          <button onClick={() => { setSevFilter(""); setTextFilter(""); }}
            className="text-xs text-indigo-600 hover:underline" data-testid="security-reset-filter">
            Reinitialiser
          </button>
        )}
      </div>

      {accessDenied.length > 0 && (
        <EventList events={accessDenied} title="Acces refuses" icon={Eye} iconColor="text-red-500" />
      )}

      <EventList events={warnings.slice(0, 30)} title="Avertissements" icon={AlertTriangle} iconColor="text-amber-500" emptyMsg="Aucun avertissement" />

      <EventList events={filtered.slice(0, 100)} title={`Tous les evenements${(sevFilter || textFilter) ? " (filtres)" : ""}`} icon={Shield} iconColor="text-gray-500" emptyMsg="Aucun evenement securitaire enregistre" />
    </div>
  );
}

export default function SecurityDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(30);
  const [tab, setTab] = useState("overview");
  const [emailCfg, setEmailCfg] = useState(null);

  useEffect(() => { loadEmailCfg(); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/security/dashboard?days=${period}`);
      setData(res.data);
    } catch (e) {
      toast.error("Impossible de charger le tableau de bord sécurité");
    }
    finally { setLoading(false); }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh toutes les 60s (donne un signal "vivant" — surtout utile en prod)
  useEffect(() => {
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const loadEmailCfg = async () => {
    try {
      const r = await api.get("/email/config");
      setEmailCfg(r.data);
    } catch { setEmailCfg(null); }
  };

  if (loading && !data) return (
    <div className="flex items-center justify-center py-20" data-testid="security-loading">
      <div className="w-8 h-8 border-3 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
    </div>
  );

  const d = data || {};
  const ls = d.login_stats || {};
  const ss = d.security_stats || {};
  const es = d.email_stats || {};

  const tabs = [
    { id: "overview", label: "Vue d'ensemble", icon: Shield },
    { id: "logins", label: "Connexions", icon: Lock },
    { id: "events", label: "Evenements", icon: AlertTriangle },
    { id: "emails", label: "Courriels", icon: Mail },
    { id: "impersonation", label: "En tant que", icon: Eye },
  ];

  // Score de sécurité simple
  const secScore = Math.max(0, 100
    - (ss.critical_count || 0) * 20
    - (ss.access_denied_count || 0) * 5
    - Math.min((ls.failed || 0) * 2, 30)
  );
  const scoreColor = secScore >= 80 ? "text-emerald-600" : secScore >= 50 ? "text-amber-600" : "text-red-600";
  const scoreBg = secScore >= 80 ? "bg-emerald-50" : secScore >= 50 ? "bg-amber-50" : "bg-red-50";

  return (
    <div className="space-y-4" data-testid="security-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-5 h-5 text-indigo-600" /> Tableau de bord Securite
          </h2>
          <p className="text-xs text-gray-500">Periode: {period} derniers jours — Genere: {formatTime(d.generated_at)}</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/audit" className="inline-flex items-center gap-1.5 border rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50" data-testid="security-audit-link">
            <History className="w-3.5 h-3.5" /> Piste d'audit
          </Link>
          <select value={period} onChange={e => setPeriod(Number(e.target.value))}
            className="border rounded-lg px-2 py-1.5 text-xs" data-testid="security-period">
            <option value={7}>7 jours</option>
            <option value={30}>30 jours</option>
            <option value={90}>90 jours</option>
          </select>
          <button onClick={load} className="border rounded-lg p-1.5 text-gray-400 hover:text-gray-600" data-testid="security-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition ${tab === t.id ? "border-indigo-600 text-indigo-700" : "border-transparent text-gray-500 hover:text-gray-700"}`}
            data-testid={`security-tab-${t.id}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {/* Overview */}
      {tab === "overview" && (
        <div className="space-y-4">
          {/* Score + Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <div className={`${scoreBg} border rounded-xl p-4 flex items-center gap-3`} data-testid="security-score">
              <div className="w-14 h-14 rounded-full border-4 border-current flex items-center justify-center flex-shrink-0" style={{ borderColor: secScore >= 80 ? "#059669" : secScore >= 50 ? "#d97706" : "#dc2626" }}>
                <span className={`text-xl font-bold ${scoreColor}`}>{secScore}</span>
              </div>
              <div>
                <p className={`text-sm font-bold ${scoreColor}`}>Score securite</p>
                <p className="text-[10px] text-gray-500">{secScore >= 80 ? "Tres bon" : secScore >= 50 ? "Attention requise" : "Action necessaire"}</p>
              </div>
            </div>
            <StatCard icon={CheckCircle} label="Connexions OK" value={ls.successful || 0} sublabel={`/${ls.total || 0} total`} color="text-emerald-600" />
            <StatCard icon={XCircle} label="Echecs connexion" value={ls.failed || 0} sublabel={`${ss.lockout_events || 0} verrouillage(s)`} color={ls.failed > 0 ? "text-red-600" : "text-gray-400"} />
            <StatCard icon={ShieldAlert} label="Alertes" value={(ss.critical_count || 0) + (ss.warning_count || 0)} sublabel={`${ss.critical_count || 0} critique(s)`} color={ss.critical_count > 0 ? "text-red-600" : "text-amber-600"} />
            <StatCard icon={Users} label="Portails actifs" value={d.active_portals || 0} sublabel={`${ls.portal_logins || 0} connexion(s)`} color="text-indigo-600" />
          </div>

          {/* Chart + Recent */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <LoginChart data={d.daily_chart || []} />
            <EventList events={(d.recent_events || []).slice(0, 15)} title="Activite recente" icon={Activity} iconColor="text-indigo-500" emptyMsg="Aucune activite securitaire enregistree" />
          </div>

          {/* Warnings */}
          {(d.critical_events || []).length > 0 && (
            <EventList events={d.critical_events} title="Evenements critiques" icon={ShieldAlert} iconColor="text-red-600" />
          )}
        </div>
      )}

      {/* Logins tab */}
      {tab === "logins" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard icon={Lock} label="Total connexions" value={ls.total || 0} color="text-gray-700" />
            <StatCard icon={ShieldCheck} label="Admin" value={ls.admin_logins || 0} color="text-blue-600" />
            <StatCard icon={Users} label="Portail" value={ls.portal_logins || 0} color="text-green-600" />
            <StatCard icon={Globe} label="IPs uniques" value={ls.unique_ips || 0} color="text-purple-600" />
          </div>

          <LoginChart data={d.daily_chart || []} />

          {/* Recent logins */}
          <div className="bg-white border rounded-xl overflow-hidden" data-testid="recent-logins">
            <div className="px-4 py-3 border-b bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Clock className="w-4 h-4 text-gray-400" /> Dernieres connexions</h3>
            </div>
            <div className="max-h-80 overflow-y-auto divide-y">
              {(d.recent_logins || []).map((l, i) => (
                <div key={i} className={`px-4 py-2.5 flex items-center gap-3 ${l.success ? "" : "bg-red-50"}`}>
                  {l.success ? <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{l.identifier}</p>
                    <p className="text-[10px] text-gray-400">{l.login_type} — {l.ip}</p>
                  </div>
                  <p className="text-[10px] text-gray-400 flex-shrink-0">{formatTime(l.timestamp)}</p>
                </div>
              ))}
              {(d.recent_logins || []).length === 0 && (
                <div className="px-4 py-8 text-center text-gray-400 text-sm">Aucune tentative de connexion enregistree</div>
              )}
            </div>
          </div>

          {/* Top IPs */}
          {(ls.top_ips || []).length > 0 && (
            <div className="bg-white border rounded-xl p-4" data-testid="top-ips">
              <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2"><Globe className="w-4 h-4 text-purple-500" /> Adresses IP</h3>
              <div className="flex flex-wrap gap-2">
                {ls.top_ips.map(ip => (
                  <span key={ip} className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs font-mono">{ip}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Events tab */}
      {tab === "events" && (
        <EventsTabContent data={d} ss={ss} />
      )}

      {/* Emails tab */}
      {tab === "impersonation" && <ImpersonationLogTab />}

      {tab === "emails" && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <StatCard icon={Mail} label="Courriels envoyes" value={es.total || 0} color="text-indigo-600" />
            <StatCard icon={CheckCircle} label="Livres" value={es.success || 0} color="text-emerald-600" />
            <StatCard icon={XCircle} label="Echoues" value={es.failed || 0} color={es.failed > 0 ? "text-red-600" : "text-gray-400"} />
          </div>
          <div className="bg-white border rounded-xl p-4" data-testid="email-info">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Configuration courriel</h3>
            <div className="space-y-1.5 text-xs text-gray-600">
              <p>Fournisseur: <span className="font-medium">{emailCfg?.provider || "WHC"} SMTP</span></p>
              <p>Serveur: <span className="font-medium">{emailCfg?.server || "-"}:{emailCfg?.port ?? "-"} ({emailCfg?.security || "-"})</span></p>
              <p>Expediteur: <span className="font-medium">{emailCfg?.user || "-"}</span></p>
              <p>Notifications cabinet: <span className="font-medium">{emailCfg?.cabinet_email || "Non configure"}</span></p>
              <p>Journal: <span className="font-medium">Retention automatique 90 jours</span></p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
